import { Check, ExpandMore } from '@mui/icons-material'
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Stack, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import ConfirmationDialog from '../../components/Dialog/ConfirmationDialog'
import LocalMonthPicker from '../../components/MonthPicker/LocalMonthPicker'
import EmployeeMonthlyHoursTable from '../../components/Table/EmployeesMonthlyHours/EmployeeMonthlyHoursTable'
import { useNotification } from '../../store/redux/hooks'
import { useSendMissingHoursNotificationMutation } from '../../store/redux/reducers/pushNotificationSlice'
import DateFormat from '../../store/types/enums/DateFormat'
import HoursSummaryEmployee from '../../store/types/models/HoursSummaryEmployee'
import { createErrorMessage } from '../../store/utils'

const EmployeeMonthlyHoursSummary = () => {
  const { t } = useTranslation()
  const [selectedDate, setSelectedDate] = useState(dayjs())
  const [selectedEmployees, setSelectedEmployees] = useState<HoursSummaryEmployee[]>([])
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(false)
  const [isNotificationSendDialogOpen, setIsNotificationSendDialogOpen] = useState(false)
  const { displayNotification } = useNotification()

  const [sendMissingHoursNotification, { isLoading: isSendMissingHoursNotificationLoading }] =
    useSendMissingHoursNotificationMutation()

  const handleSendNotification = async () => {
    try {
      await sendMissingHoursNotification({
        employeeIds: selectedEmployees.map(e => e.id),
        month: selectedDate.format(DateFormat.YearMonth),
      }).unwrap()

      displayNotification({
        type: 'success',
        message: t('components.notificationSidebar.missingHoursNotification.successMessage'),
      })

      setSelectedEmployees([])
      setIsNotificationSendDialogOpen(false)
    } catch (err) {
      const errorMessage = createErrorMessage(err)
      displayNotification({
        message: errorMessage || t('components.notificationSidebar.missingHoursNotification.errorMessage'),
        type: 'error',
      })
    }
  }

  return (
    <>
      <Accordion
        slotProps={{ transition: { unmountOnExit: true } }}
        sx={{
          boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px',
          bgcolor: 'background.elevation1',
          color: 'text.highEmphasis',
          backgroundImage: 'none',
        }}
        expanded={isAccordionExpanded}
        onChange={(_, expanded) => setIsAccordionExpanded(expanded)}
      >
        <AccordionSummary
          id="monthly-hours-summary-header"
          aria-controls="monthly-hours-summary-content"
          expandIcon={<ExpandMore />}
          sx={{ px: 3 }}
        >
          <Typography variant="h5">{t('page.adminsPage.generalSettings.employeeMonthlyHours.hoursSummary')}</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 3 }}>
          <Stack gap={2} alignItems={'center'}>
            <Stack
              width="100%"
              gap={2}
              sx={{
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'center', sm: 'center' },
              }}
              flexWrap="wrap"
            >
              <Box sx={{ flex: { xs: '0 0 auto', sm: 1 } }} />

              <Box sx={{ display: 'flex', justifyContent: 'center', width: { sm: '100%', md: 'auto' } }}>
                <LocalMonthPicker value={selectedDate} onChange={date => setSelectedDate(date)} />
              </Box>

              <Box
                sx={{
                  flex: { xs: '0 0 auto', sm: 1 },
                  display: 'flex',
                  justifyContent: { sm: 'center', md: 'flex-end' },
                }}
              >
                <Button
                  onClick={() => setIsNotificationSendDialogOpen(prev => !prev)}
                  size="small"
                  color="primary"
                  variant="contained"
                  disabled={selectedEmployees.length === 0}
                  sx={{ whiteSpace: { xs: 'wrap', sm: 'nowrap' }, minHeight: { xs: 'max-content', sm: 'auto' } }}
                >
                  <Check sx={{ fontSize: '15px', right: '1px', position: 'relative' }} />
                  {t('page.adminsPage.generalSettings.employeeMonthlyHours.notify')}
                </Button>
              </Box>
            </Stack>
            <EmployeeMonthlyHoursTable
              selectedDate={selectedDate}
              selectedEmployees={selectedEmployees}
              setSelectedEmployees={setSelectedEmployees}
              enabled={isAccordionExpanded}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      <ConfirmationDialog
        open={isNotificationSendDialogOpen}
        setOpen={setIsNotificationSendDialogOpen}
        handleConfirm={handleSendNotification}
        title={t('components.notificationSidebar.dialog.sendNotificationTitle')}
        content={
          <Stack gap={1}>
            {t('components.notificationSidebar.dialog.sendNotificationContent')}
            <Stack>
              {selectedEmployees.map(employee => (
                <Typography key={employee.id} variant="body2">
                  - {employee.name}
                </Typography>
              ))}
            </Stack>
          </Stack>
        }
        loading={isSendMissingHoursNotificationLoading}
      />
    </>
  )
}

export default EmployeeMonthlyHoursSummary
